import { useEffect, useState, Fragment } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { fmtTime, FLAG_LABELS } from "../lib/format";
import { Badge } from "../components/ui/badge";
import PeriodSelector, { rangeFor } from "../components/PeriodSelector";
import { toast } from "sonner";

const FLAG_STYLES = {
  missed_clockout: "bg-[#F87171]/10 text-[#F87171]",
  auto_clockout: "bg-[#D99A29]/15 text-[#8a6414]",
  over_roster: "bg-[#D99A29]/15 text-[#8a6414]",
  public_holiday: "bg-[#A9C97E]/10 text-[#A9C97E]",
  no_show: "bg-[#F87171]/10 text-[#F87171]",
  over_9h_day: "bg-[#595959]/10 text-[#595959]",
  over_45h_week: "bg-[#595959]/10 text-[#595959]",
  phone_no_gps: "bg-[#A78BFA]/10 text-[#A78BFA]",
};

export default function Hours() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(rangeFor("month"));
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get(`/hours/summary?start=${range[0]}&end=${range[1]}`)
      .then((r) => setSummary(r.data))
      .catch(() => toast.error("Failed to load hours summary"));
    api.get(`/hours/audit?start=${range[0]}&end=${range[1]}`)
      .then((r) => setDetail(r.data.rows))
      .catch(() => {});
  }, [range]);

  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") setRange(rangeFor(p));
  };

  const rowsFor = (empId) => detail.filter((r) => r.employee_id === empId);

  return (
    <div className="space-y-6" data-testid="hours-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Hours</h1>
          <p className="text-muted-foreground mt-1">
            Scheduled vs actual per staff member · over/under-time · weekly cap {summary ? `${summary.weekly_hours_cap}h` : "…"}. Click a row for the day-by-day detail.
          </p>
        </div>
        <PeriodSelector preset={preset} start={range[0]} end={range[1]} onPreset={onPreset} onRange={setRange} testid="hours-period" />
      </div>

      {!summary ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="glass border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="hours-summary-table">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium w-8"></th>
                <th className="px-4 py-3 font-medium">Staff member</th>
                <th className="px-4 py-3 font-medium text-right">Days</th>
                <th className="px-4 py-3 font-medium text-right">Scheduled</th>
                <th className="px-4 py-3 font-medium text-right">Actual</th>
                <th className="px-4 py-3 font-medium text-right">Over / under</th>
                <th className="px-4 py-3 font-medium text-right" title={`Hours above the ${summary.weekly_hours_cap}h weekly cap`}>Overtime (cap)</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <Fragment key={r.employee_id}>
                  <tr data-testid={`hours-summary-${r.employee_id}`} onClick={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)}
                    className="border-b last:border-0 cursor-pointer hover:bg-accent/60 transition-colors">
                    <td className="px-4 py-2.5">{expanded === r.employee_id ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
                    <td className="px-4 py-2.5 font-medium">{r.employee_name}<span className="block text-[10px] text-muted-foreground font-normal">{r.position}</span></td>
                    <td className="px-4 py-2.5 num text-right">{r.days_worked}</td>
                    <td className="px-4 py-2.5 num text-right">{r.scheduled_hours}h</td>
                    <td className="px-4 py-2.5 num text-right">{r.actual_hours}h</td>
                    <td className={`px-4 py-2.5 num text-right ${r.diff_hours > 0 ? "text-[#8a6414]" : r.diff_hours < 0 ? "text-[#F87171]" : ""}`} data-testid={`hours-diff-${r.employee_id}`}>
                      {r.diff_hours > 0 ? "+" : ""}{r.diff_hours}h
                    </td>
                    <td className="px-4 py-2.5 num text-right" data-testid={`hours-ot-${r.employee_id}`}>
                      {r.overtime_hours > 0 ? (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#F87171]/10 text-[#F87171]">+{r.overtime_hours}h OT</span>
                      ) : "—"}
                    </td>
                  </tr>
                  {expanded === r.employee_id && (
                    <tr data-testid={`hours-detail-${r.employee_id}`}>
                      <td colSpan={7} className="bg-accent/30 px-6 py-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-muted-foreground border-b border-border/60">
                              <th className="py-1.5 font-medium">Date</th>
                              <th className="py-1.5 font-medium text-right">Sched</th>
                              <th className="py-1.5 font-medium text-right">Actual</th>
                              <th className="py-1.5 font-medium">In</th><th className="py-1.5 font-medium">Out</th>
                              <th className="py-1.5 font-medium">Flags</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowsFor(r.employee_id).map((d, i) => (
                              <tr key={`${d.date}-${i}`} className="border-b last:border-0 border-border/40" data-testid={`hours-detail-row-${r.employee_id}-${d.date}`}>
                                <td className="py-1.5">{d.date}{d.holiday_name && <span className="block text-[9px] text-[#A9C97E]">{d.holiday_name}</span>}</td>
                                <td className="py-1.5 num text-right">{d.scheduled_hours ? `${d.scheduled_hours}h` : "—"}</td>
                                <td className="py-1.5 num text-right">{d.actual_hours ? `${d.actual_hours}h` : "—"}</td>
                                <td className="py-1.5">{fmtTime(d.clock_in)}</td>
                                <td className="py-1.5">{fmtTime(d.clock_out)}</td>
                                <td className="py-1.5">
                                  <div className="flex flex-wrap gap-1">
                                    {d.flags.map((f) => (
                                      <Badge key={f} variant="outline" className={`border-0 text-[9px] ${FLAG_STYLES[f] || ""}`} data-testid={`flag-${f}`}>
                                        {FLAG_LABELS[f] || f}
                                      </Badge>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {rowsFor(r.employee_id).length === 0 && (
                              <tr><td colSpan={6} className="py-3 text-muted-foreground">No day-level records in this period.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {summary.rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No rostered or clocked hours in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Overtime uses the same weekly-hours-cap logic as the Roster conflict flags and Staff Directory OT. Day-level flags (missed/auto clock-outs, over-roster, no-shows, public holidays) are in the drill-down.
      </p>
    </div>
  );
}
